import ReactDOM from "react-dom";
import React, { FC, useLayoutEffect, useRef, useState } from "react";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { AdvertiserType } from "../../../../types/advertiserTypes";
import { ROUTE_PATHS } from "../../../../types/RoutePaths";
import { ReduxApplicationState } from "../../../../types/ReduxTypes/ReduxApplicationState";
import { selectGlobalAdvertiser } from "../../../../redux/reducers/agency/actions";

type ProfileAdvertiserSelectorProps = {
  onAdvertiserSelected?: () => void;
};

const Section = styled("div")(({ theme }) => ({
  padding: "8px 0",
  background: theme.palette.brand.gray50,
  borderTop: `1px solid ${theme.palette.brand.gray200}`,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
}));

const SectionLabel = styled("div")(({ theme }) => ({
  fontSize: 10,
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  color: theme.palette.brand.gray500,
  fontWeight: 600,
  padding: "0 16px 4px",
}));

const AdvertiserRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "8px 16px",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
  "&:hover": {
    background: theme.palette.brand.gray100,
  },
  '&[data-active="true"]': {
    background: theme.palette.brand.gray100,
  },
}));

const AdvertiserName = styled("span")({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
  flex: 1,
});

const Chevron = styled("span")(({ theme }) => ({
  color: theme.palette.brand.gray400,
  fontSize: 16,
  lineHeight: 1,
  flexShrink: 0,
}));

const FlyoutPanel = styled("div")(({ theme }) => ({
  position: "fixed",
  minWidth: 240,
  maxWidth: 320,
  maxHeight: 360,
  overflowY: "auto" as const,
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  boxShadow: "0 8px 24px rgba(15, 43, 76, 0.12)",
  zIndex: 1400,
  padding: "8px 0",
}));

const FlyoutHeader = styled("div")(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.brand.blueAccent,
  padding: "8px 16px 6px",
}));

const FlyoutItem = styled("button")(({ theme }) => ({
  display: "block",
  width: "100%",
  border: "none",
  background: "transparent",
  textAlign: "left" as const,
  padding: "10px 16px",
  fontSize: 13,
  color: theme.palette.brand.gray700,
  cursor: "pointer",
  fontFamily: "inherit",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
  "&:hover": {
    background: theme.palette.brand.gray50,
    color: theme.palette.brand.gray900,
  },
  '&[data-selected="true"]': {
    background: theme.palette.brand.gray100,
    color: theme.palette.brand.gray900,
    fontWeight: 600,
  },
}));

const EmptyState = styled("div")(({ theme }) => ({
  padding: "10px 16px",
  fontSize: 13,
  color: theme.palette.brand.gray500,
}));

const ADVERTISER_DASHBOARD_PATH = new RegExp(`^${ROUTE_PATHS.ADVERTISER_DASHBOARD.replace(":id", "([^/]+)")}$`);

const ProfileAdvertiserSelector: FC<ProfileAdvertiserSelectorProps> = ({ onAdvertiserSelected }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const advertisers = useSelector((state: ReduxApplicationState) => state.agency.advertisers);
  const selectedAdvertiserId = useSelector((state: ReduxApplicationState) => state.agency.selectedAdvertiserId);
  const selectedAdvertiser = advertisers.find((advertiser) => advertiser.brandgenAdvertiserId === selectedAdvertiserId);

  const rowRef = useRef<HTMLDivElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const [flyoutPosition, setFlyoutPosition] = useState({ top: 0, left: 0 });

  const sortedAdvertisers = [...advertisers].sort((a, b) => a.name.localeCompare(b.name));

  const clearCloseTimer = () => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openFlyout = () => {
    clearCloseTimer();
    setIsFlyoutOpen(true);
  };

  const scheduleFlyoutClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setIsFlyoutOpen(false);
      closeTimerRef.current = null;
    }, 150);
  };

  useLayoutEffect(() => {
    if (!isFlyoutOpen || !rowRef.current) return;

    const rowRect = rowRef.current.getBoundingClientRect();
    const flyoutWidth = flyoutRef.current?.offsetWidth ?? 240;
    const flyoutHeight = flyoutRef.current?.offsetHeight ?? 280;
    const gap = 8;

    let left = rowRect.left - flyoutWidth - gap;
    if (left < 8) {
      left = rowRect.right + gap;
    }

    let top = rowRect.top - 8;
    if (top + flyoutHeight > window.innerHeight - 16) {
      top = window.innerHeight - flyoutHeight - 16;
    }
    top = Math.max(8, top);

    setFlyoutPosition({ top, left });
  }, [isFlyoutOpen, sortedAdvertisers.length]);

  const handleSelectAdvertiser = (advertiser: AdvertiserType) => {
    dispatch(selectGlobalAdvertiser(advertiser));
    setIsFlyoutOpen(false);
    onAdvertiserSelected?.();

    const dashboardMatch = location.pathname.match(ADVERTISER_DASHBOARD_PATH);
    if (dashboardMatch && dashboardMatch[1] !== advertiser.brandgenAdvertiserId) {
      history.replace(`/advertisers/${advertiser.brandgenAdvertiserId}/dashboard`);
    }
  };

  if (!advertisers.length) {
    return (
      <Section>
        <SectionLabel>Advertiser</SectionLabel>
        <EmptyState>No advertisers available</EmptyState>
      </Section>
    );
  }

  return (
    <Section onMouseLeave={scheduleFlyoutClose}>
      <SectionLabel>Advertiser</SectionLabel>
      <AdvertiserRow
        ref={rowRef}
        data-active={isFlyoutOpen ? "true" : "false"}
        onMouseEnter={openFlyout}
        onMouseLeave={scheduleFlyoutClose}
      >
        <AdvertiserName>{selectedAdvertiser?.name ?? "Select advertiser"}</AdvertiserName>
        <Chevron>›</Chevron>
      </AdvertiserRow>

      {isFlyoutOpen &&
        ReactDOM.createPortal(
          <FlyoutPanel
            ref={flyoutRef}
            style={{ top: flyoutPosition.top, left: flyoutPosition.left }}
            onMouseEnter={openFlyout}
            onMouseLeave={scheduleFlyoutClose}
          >
            <FlyoutHeader>All advertisers</FlyoutHeader>
            {sortedAdvertisers.map((advertiser) => (
              <FlyoutItem
                key={advertiser.brandgenAdvertiserId}
                type="button"
                data-selected={advertiser.brandgenAdvertiserId === selectedAdvertiserId ? "true" : "false"}
                onClick={() => handleSelectAdvertiser(advertiser)}
              >
                {advertiser.name}
              </FlyoutItem>
            ))}
          </FlyoutPanel>,
          document.body,
        )}
    </Section>
  );
};

export default ProfileAdvertiserSelector;
